/**
 * Shared Donezo / forest-green theme for Table View & Graph View filter panels.
 * Colors use CSS variables from `.donezo-table-view` (light + dark via SCSS).
 */

export const SAMPLING_THEME = {
  primary: "#1b5e20",
  primaryDark: "#14532d",
  primaryLight: "#dcfce7",
  primaryMuted: "#f0fdf4",
  focusRing: "rgba(27, 94, 32, 0.12)",
  textActive: "#1b5e20",
  textMuted: "#64748b",
  border: "#e5e7eb",
  borderLight: "#f0f0f0",
  controlBg: "#f9fafb",
  iconPond: "#1b5e20",
  iconFish: "#22c55e",
  iconDate: "#15803d",
  iconNeutral: "#6b7280",
};

/** @deprecated Use className `donezo-sampling-filter` — kept for gradual migration */
export const samplingFilterPanelStyle = {};

export const samplingFilterAccentBarStyle = {};

export const samplingFilterBodyStyle = {};

export const samplingFilterLabelRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  marginBottom: 4,
};

export const samplingFilterLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--sf-label, #64748b)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

export const samplingFetchButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  height: 40,
  padding: "0 22px",
  borderRadius: 24,
  border: "none",
  background: SAMPLING_THEME.primary,
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(27, 94, 32, 0.25)",
  transition: "all 0.2s",
  flex: 1,
};

export function samplingCheckboxLabelStyle(checked) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    border: checked
      ? `1.5px solid ${SAMPLING_THEME.primary}`
      : `1.5px solid var(--sf-checkbox-border, ${SAMPLING_THEME.border})`,
    backgroundColor: checked
      ? "var(--sf-checkbox-bg-active, #f0fdf4)"
      : "var(--sf-checkbox-bg, #f9fafb)",
    cursor: "pointer",
    transition: "all 0.15s",
    flexShrink: 0,
  };
}

export function samplingCheckboxTextStyle(checked) {
  return {
    fontSize: 13,
    fontWeight: 600,
    color: checked ? SAMPLING_THEME.textActive : "var(--sf-label, #64748b)",
    whiteSpace: "nowrap",
  };
}

export const samplingCheckboxInputStyle = {
  width: 15,
  height: 15,
  accentColor: SAMPLING_THEME.primary,
  cursor: "pointer",
};

/** react-select styles aligned with Donezo green theme + dark mode CSS variables */
export function createSamplingFilterSelectStyles(DropDownStyles) {
  return {
    ...DropDownStyles,
    control: (provided, state) => ({
      ...DropDownStyles.control(provided, state),
      borderRadius: "10px",
      border: state.isFocused
        ? `1.5px solid ${SAMPLING_THEME.primary}`
        : `1.5px solid var(--sf-control-border, ${SAMPLING_THEME.border})`,
      minHeight: "36px",
      fontSize: "13px",
      backgroundColor: "var(--sf-control-bg, #f9fafb)",
      color: "var(--sf-control-text, #111827)",
      boxShadow: state.isFocused
        ? `0 0 0 3px ${SAMPLING_THEME.focusRing}`
        : "0 1px 2px rgba(0,0,0,0.04)",
      transition: "border-color 0.15s, box-shadow 0.15s",
      "&:hover": { borderColor: "#9ca3af" },
    }),
    menu: (provided) => ({
      ...DropDownStyles.menu(provided),
      borderRadius: "12px",
      fontSize: "13px",
      backgroundColor: "var(--sf-option-bg, #fff)",
      boxShadow: "0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
      border: `1px solid var(--sf-control-border, ${SAMPLING_THEME.border})`,
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused || state.isSelected
        ? "var(--sf-option-hover, #f0fdf4)"
        : "var(--sf-option-bg, #fff)",
      color: state.isSelected
        ? SAMPLING_THEME.textActive
        : "var(--sf-control-text, #334155)",
      cursor: "pointer",
      fontSize: "13px",
      padding: "8px 12px",
      "&:active": { backgroundColor: "var(--sf-option-hover, #f0fdf4)" },
    }),
    valueContainer: (provided) => ({ ...provided, padding: "0 8px" }),
    indicatorsContainer: (provided) => ({
      ...provided,
      "& > div": { padding: "4px 6px" },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "var(--sf-control-placeholder, #9ca3af)",
      fontSize: "13px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--sf-control-text, #111827)",
      fontWeight: 500,
      fontSize: "13px",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "var(--sf-option-hover, #f0fdf4)",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "var(--sf-control-text, #111827)",
    }),
    input: (provided) => ({
      ...provided,
      color: "var(--sf-control-text, #111827)",
    }),
  };
}
